/**
 * @file Person detail (`#/people/:id`) — admin work surface for one employee.
 *
 * One page, three roles depending on the employee's phase:
 *   - onboarding: shopping-list workflow inline (mark ordered / prepared /
 *     handed over per item)
 *   - offboarding: device buy/return decisions inline + resale values
 *   - active or done: just identity, gear, history
 *
 * The old `#/onboarding/:id` and `#/offboarding/:id` routes redirect here.
 * The standalone detail pages were removed; the workflow rendering lives
 * here. The onboarding/offboarding list pages still exist as filtered
 * queue views of People.
 */

import { el, clear, formatDate, formatCZK, toast, withButtonBusy } from '../utils.js';
import { Icons, categoryIcon } from '../icons.js';
import {
  CATEGORIES, OFFBOARDING_STATUS, ONBOARDING_STATUS,
  PHASES, TARIFF_SUBCATEGORIES, contextTagInfo,
} from '../config.js';
import { navigate } from '../router.js';
import { getCurrentUser } from '../auth.js';
import {
  getEmployee, getHandoversForEmployee, watchDevicesForHolder, watchDevices,
  updateOnboardingItem, watchOnboarding,
  getOffboarding, setDeviceDecision, setAssetDecision, recordHandover,
  getConfig, watchPendingChanges,
} from '../db.js';
import { calculateResaleValue, defaultFormulas } from '../formula.js';
import { handoverDevice, submitGearAction, finishOnboarding, sendToLoot } from '../api.js';
import { reportLootResult } from '../lib/loot.js';
import { logAuditEvent } from '../audit-log.js';
import { AUDIT_ACTION }  from '../lib/audit-actions.js';
import { renderPageHead }      from '../page-header.js';
import { renderHeldDeviceRow } from '../lib/device-row.js';
import { renderBackLink }      from '../lib/back-link.js';
import { copyable }            from '../lib/copyable.js';
import { renderLoadError }     from '../lib/load-error.js';
import { pageToken, isStale, remountPage } from '../lib/page-lifecycle.js';
import { gearActionLabel }     from '../lib/gear-actions.js';
import { normaliseSnForMatch } from '../lib/sn-match.js';
import { catRank }             from '../lib/category-order.js';
import { kvRow }                from '../lib/kv.js';
import { promptForPrice }      from '../lib/price-modal.js';
import { promptText }          from '../lib/text-modal.js';
import { renderPager, bindPagerKeys } from '../lib/pager.js';
import { downloadFinanceCsv }  from '../lib/finance-csv.js';
import {
  DEVICE_HANDOVER_TRIGGER_STATUSES, PHONE_TARIFF_TERMINAL_STATUSES,
} from '../lib/offboarding-statuses.js';
import { confirmDialog } from '../standard/js/dialog.js';

// Offboarding terminal states → which pending gear action fires. The
// action is submitted FIRST (pings Slack, creates the pending_changes row
// for Abra reconciliation); only on success do we record the local
// decision + handover. `bought` needs an admin-entered price. `to_loot` is
// Send to Loot instead (Loot sets the price; the sale reaches Pending Abra
// when Loot hands the device over).
const OFFBOARDING_TERMINAL_TO_GEAR_ACTION = {
  bought:     'sell',
  in_storage: 'takeback',
  to_testing: 'make_testing',
  // `returned` is intermediate (the admin then picks storage/loot/testing) —
  // no gear action fires here, by design.
};
const TERMINAL_NEEDS_PRICE = new Set(['bought']);
// Device-branch statuses that write a /handovers row (shared constant).
const TERMINAL_OFFBOARDING_STATUSES = new Set(DEVICE_HANDOVER_TRIGGER_STATUSES);

export async function renderPersonDetail(host, employeeId) {
  clear(host);
  // Back link + Prev / Next through the list this person was opened from
  // (People / Onboarding / Offboarding, in that list's current order).
  const toPath = (id) => `/people/${id}`;
  host.appendChild(el('div', { class: 'page-nav' }, backLink(), renderPager({ id: employeeId, toPath })));
  bindPagerKeys(host, { id: employeeId, toPath });

  const skel = el('div', { class: 'skeleton', style: 'height:240px' });
  host.appendChild(skel);
  const token = pageToken(host);

  // onboarding / offboarding docs are fetched inside their own workflow
  // renderers now (so an action re-renders just that card with fresh
  // data), so we don't load them here.
  let employee; let handovers; let formulasDoc;
  try {
    [employee, handovers, formulasDoc] = await Promise.all([
      getEmployee(employeeId),
      getHandoversForEmployee(employeeId).catch((err) => {
        // Don't render a lying "No handovers logged yet" over a query failure.
        console.error('[person-detail] history load failed', err);
        return [];
      }),
      getConfig('resaleFormulas').catch(() => null),
    ]);
  } catch (err) {
    // The employee fetch has no fallback, so an error here used to strand the
    // admin on an endless skeleton with only a console rejection.
    console.error('[person-detail] load failed', err);
    if (isStale(token)) return;
    skel.remove();
    host.appendChild(renderLoadError('Could not load this person.', () => renderPersonDetail(host, employeeId)));
    return;
  }
  // The user navigated away while we awaited — the router already mounted
  // another page; appending now would stack this one into it.
  if (isStale(token)) return;
  skel.remove();
  const formulas = formulasDoc || defaultFormulas();

  if (!employee) {
    host.appendChild(el('div', { class: 'empty' }, el('p', null, 'Person not found.')));
    return;
  }

  host.appendChild(renderPageHead(
    employee.fullName || '(no name)',
    el('p', { class: 'muted' },
      employee.email ? copyable(employee.email, { label: 'email' }) : 'no email', ' · ',
      el('span', { class: `chip ${phaseClass(employee.phase)}` }, phaseLabel(employee.phase)),
    ),
  ));

  const grid = el('div', { class: 'detail-grid' });
  host.appendChild(grid);

  // ── Side: identity ───────────────────────────────────────────────────
  const side = el('aside', { class: 'detail-side profile-side' });
  grid.appendChild(side);

  side.appendChild(el('div', { class: 'card' },
    el('div', { class: 'section-head' }, 'Identity'),
    el('table', { class: 'kv' },
      kvRow('Job title',  employee.jobTitle || '-'),
      kvRow('Department', employee.department || '-'),
      kvRow('Emp. number', employee.employeeNumber ? copyable(employee.employeeNumber, { label: 'employee number', mono: true }) : '-', false, true),
      kvRow('Start',      formatDate(employee.startDate)),
      kvRow('End',        formatDate(employee.endDate)),
    ),
  ));

  // ── Main column ─────────────────────────────────────────────────────
  const main = el('div', { class: 'stack' });
  grid.appendChild(main);

  // Currently-held devices (live). The employeeId is stashed on the body
  // element so renderHeldRow can detect "viewing-from-the-other-side" cases
  // when a device has a holder mismatch.
  const devicesCard = el('section', { class: 'card' });
  const devicesHead = el('div', { class: 'section-head' }, 'Currently held', el('span', { class: 'count' }, '(0)'));
  const devicesBody = el('div');
  devicesBody.dataset.employeeId = employeeId;
  devicesCard.appendChild(devicesHead);
  devicesCard.appendChild(devicesBody);
  // The person's own laptops (marked personal): listed for reference, never
  // counted as held company gear. Hidden when there are none.
  const personalHost = el('div');
  devicesCard.appendChild(personalHost);
  // Hand over a device any time, outside onboarding — a mouse a year in, a
  // renewal laptop, a first phone once a new model is out. Peripherals are
  // created in Gear on the spot; laptops/phones go to Finance as a `register`
  // pending action and land under "Pending in Abra" (see openAddGearModal).
  if (employee.phase !== PHASES.done) {
    const addGearBtn = el('button', { class: 'btn btn-sm', style: 'margin-top:12px' },
      Icons.plus(), 'Hand over a device');
    addGearBtn.addEventListener('click', () => openAddGearModal(employeeId, employee));
    devicesCard.appendChild(addGearBtn);
  }
  main.appendChild(devicesCard);

  // ── Pending in Abra (admin overlay) ────────────────────────────────────
  // Every "the intent exists but /devices doesn't show it yet" row that names
  // this person, in yellow — straight from /pending_changes:
  //   • gear actions (hand-over / transfer / take-back / sale) on an EXISTING
  //     device — Abra hasn't caught up, so /devices doesn't list them as
  //     holder yet. INCOMING (they're the target holder) or OUTGOING.
  //   • `register` rows — a laptop/phone Abra doesn't have yet, handed over
  //     from the onboarding checklist or the profile dialog. No /devices doc
  //     exists until Finance creates the asset, so this is the only place the
  //     device shows up meanwhile.
  // Self-healing: the reconciler flips the row to 'reconciled' once Abra
  // matches and the device moves into "Currently held" on its own. Admin-only
  // page (employees are bounced to /me), so users never see this section.
  const pendingCard = el('section', { class: 'card', style: 'display:none' });
  const pendingHead = el('div', { class: 'section-head' },
    'Pending in Abra', el('span', { class: 'count' }, '(0)'));
  const pendingBody = el('div');
  pendingCard.appendChild(pendingHead);
  pendingCard.appendChild(pendingBody);
  main.appendChild(pendingCard);

  let liveDevices = [];
  let onboardingHost = null; // set below when phase === onboarding

  function refreshPendingSection(rows) {
    const mine = [];
    for (const r of rows) {
      const relation = pendingRelationToPerson(r, employeeId);
      if (relation) mine.push({ row: r, relation });
    }
    renderPendingForPerson(pendingCard, pendingHead, pendingBody, mine);
  }

  const unsub = watchDevicesForHolder(employeeId, (devices) => {
    liveDevices = devices;
    renderHeldDevices(devicesHead, devicesBody, devices);
    // Offboarding workflow depends on the live device list — re-render it.
    if (offboardingHost) renderOffboardingWorkflow(offboardingHost, employeeId, employee, devices, formulas);
  });
  host.addEventListener('cleanup', unsub, { once: true });

  const unsubPersonal = watchDevices({ currentHolder: employeeId }, (devices) => {
    // One row per physical laptop: a Mac enrolled in both MDMs has an IRU
    // and a Meraki doc for the same serial (IRU preferred, it's the future).
    const bySn = new Map();
    for (const d of devices.filter((x) => x.personal)) {
      const k = normaliseSnForMatch(d.sn) || d.id;
      if (!bySn.has(k) || d.source === 'iru') bySn.set(k, d);
    }
    const own = [...bySn.values()];
    clear(personalHost);
    if (!own.length) return;
    personalHost.appendChild(el('div', { class: 'section-head', style: 'margin-top:20px' },
      'Personal', el('span', { class: 'count' }, `(${own.length})`)));
    for (const d of own) {
      personalHost.appendChild(renderHeldDeviceRow(d, {
        admin: true, viewingEmployeeId: employeeId, onClick: () => navigate(`/devices/${d.id}`),
      }));
    }
  });
  host.addEventListener('cleanup', unsubPersonal, { once: true });

  const unsubPending = watchPendingChanges({ status: 'pending' }, (rows) => {
    refreshPendingSection(rows);
  });
  host.addEventListener('cleanup', unsubPending, { once: true });

  const unsubOnboarding = watchOnboarding(employeeId, (items) => {
    // Re-render the workflow card straight from the live snapshot. onSnapshot
    // only ever delivers COMMITTED data, so an item action advances the row
    // immediately — unlike a getDoc right after the write's transaction, which
    // could read stale local cache (the "button doesn't move until I click
    // another" bug). Fires on the initial subscribe too, so no separate mount
    // render is needed.
    if (onboardingHost) renderOnboardingWorkflow(onboardingHost, employeeId, employee, host, items);
  });
  host.addEventListener('cleanup', unsubOnboarding, { once: true });

  // Onboarding workflow (only when phase matches). The card is rendered by the
  // watchOnboarding listener above (which fires immediately on subscribe).
  if (employee.phase === PHASES.onboarding) {
    onboardingHost = el('section', { class: 'card workflow-card' });
    main.appendChild(onboardingHost);
  }

  // Phone tariffs — Bamboo's customAssets table is the source of truth.
  // Visible for every phase: active employees see what tariff they hold,
  // leavers get action buttons for the wants_to_keep / wants_to_cancel
  // workflow. The "active" filter is no-return-date in Bamboo; finance
  // closing the asset there makes the row disappear next sync.
  const activeSimCards = (employee.assets || [])
    .filter((a) => a.category === 'Sim Card' && !a.dateReturned);
  if (activeSimCards.length) {
    const phoneTariffHost = el('section', { class: 'card' });
    main.appendChild(phoneTariffHost);
    renderPhoneTariffWorkflow(
      phoneTariffHost, employeeId, employee, activeSimCards,
      employee.phase === PHASES.offboarding, // show action buttons only in offboarding
    );
  }

  // Offboarding device workflow (only when phase matches)
  let offboardingHost = null;
  if (employee.phase === PHASES.offboarding) {
    offboardingHost = el('section', { class: 'card workflow-card' });
    main.appendChild(offboardingHost);
    renderOffboardingWorkflow(offboardingHost, employeeId, employee, liveDevices, formulas);
  }

  // History (always)
  main.appendChild(el('section', { class: 'card' },
    el('div', { class: 'section-head' }, 'History', el('span', { class: 'count' }, `(${handovers.length})`)),
    handovers.length
      ? el('div', { class: 'stack', style: 'gap:8px' },
          ...handovers.map(renderHandoverRow))
      : el('p', { class: 'muted' }, 'No handovers logged yet.'),
  ));
}

// ── Held devices ───────────────────────────────────────────────────────
function renderHeldDevices(headEl, bodyEl, devices) {
  headEl.querySelector('.count').textContent = `(${devices.length})`;
  clear(bodyEl);
  if (!devices.length) {
    bodyEl.appendChild(el('p', { class: 'muted' }, 'No devices currently assigned.'));
    return;
  }
  const sorted = [...devices].sort((a, b) => {
    const ra = catRank(a.category), rb = catRank(b.category);
    if (ra !== rb) return ra - rb;
    return (a.name || '').localeCompare(b.name || '');
  });
  for (const d of sorted) {
    bodyEl.appendChild(renderHeldDeviceRow(d, {
      admin: true,
      viewingEmployeeId: bodyEl.dataset.employeeId || null,
      onClick: () => navigate(`/devices/${d.id}`),
    }));
  }
}

// ── Pending-in-Abra overlay (admin) ────────────────────────────────────
// Which side of a pending gear action this person is on, or null if the
// action doesn't name them. Incoming = they're the (future) holder — a
// hand-over / transfer target, or a sale buyer. Outgoing = the device is
// leaving them — a transfer source or a take-back. dispose / make_testing
// carry no holder, so they never match (correctly absent here).
function pendingRelationToPerson(row, personId) {
  const incomingId = row.toHolder?.employeeId || row.saleData?.buyer?.employeeId || null;
  if (incomingId === personId) return 'incoming';
  if (row.fromHolder?.employeeId === personId) return 'outgoing';
  return null;
}

function renderPendingForPerson(card, headEl, bodyEl, items) {
  clear(bodyEl);
  if (!items.length) { card.style.display = 'none'; return; }
  card.style.display = '';
  headEl.querySelector('.count').textContent = `(${items.length})`;
  // Incoming first, then by device name — a stable, predictable order.
  items.sort((a, b) =>
    (a.relation === b.relation ? 0 : a.relation === 'incoming' ? -1 : 1)
    || (a.row.device?.name || '').localeCompare(b.row.device?.name || ''));
  for (const { row, relation } of items) {
    bodyEl.appendChild(renderPendingDeviceRow(row, relation));
  }
}

// Abra-owned categories. Gear never mints a /devices doc for these: Finance
// registers them in Abra and the Flexi sync materialises the real record. A
// hand-over of one — from the onboarding checklist or the profile dialog —
// is a `register` pending action, never a Gear device.
const ABRA_OWNED = new Set(['laptop', 'phone']);

function renderPendingDeviceRow(row, relation) {
  const dev = row.device || {};
  const cat = CATEGORIES[dev.category] || { label: dev.category || 'Device' };
  // A `register` row has no device page until Abra creates the asset (the
  // reconciler pins deviceId on first sighting) — not clickable until then.
  const link = row.deviceId
    ? { style: 'cursor:pointer', onClick: () => navigate(`/devices/${row.deviceId}`), 'data-row-link': true }
    : {};
  return el('div', { class: 'item item-warn', ...link },
    el('span', { class: 'cat-icon' }, categoryIcon(dev.category)),
    el('div', null,
      el('div', { class: 'name' }, dev.name || cat.label),
      el('div', { class: 'meta' },
        pendingDirectionText(row, relation),
        dev.sn
          ? copyable(dev.sn, { label: 'SN', mono: true, style: 'margin-left:8px', display: `SN ${dev.sn}` })
          : null,
      ),
    ),
    el('span', { class: 'chip chip-warning' }, 'Pending in Abra'),
    el('span', { class: 'chips' }),
  );
}

function pendingDirectionText(row, relation) {
  const label = (gearActionLabel(row.action) || row.action || 'change').toLowerCase();
  if (relation === 'incoming') {
    if (row.action === 'register') return 'New device';
    if (row.action === 'transfer' && row.fromHolder?.name) return `Pending transfer in — from ${row.fromHolder.name}`;
    if (row.action === 'sell') return 'Pending sale to this person';
    return `Pending ${label}`;
  }
  // outgoing
  if (row.action === 'transfer' && row.toHolder?.name) return `Pending transfer out — to ${row.toHolder.name}`;
  if (row.action === 'takeback') return 'Pending take-back — to storage';
  if (row.action === 'sell') return 'Pending sale';
  return `Pending ${label} (outgoing)`;
}

// ── Onboarding workflow (inline) ───────────────────────────────────────
function renderOnboardingWorkflow(card, employeeId, employee, host, items = []) {
  // Rendered from the live /onboardings snapshot (watchOnboarding in
  // renderPersonDetail). Item actions just write; the listener re-renders this
  // card with committed data — so no manual re-fetch (a getDoc right after the
  // write's transaction can read stale cache) and no self-invoking onChange.
  clear(card);
  card.appendChild(el('div', { class: 'section-head' },
    'Onboarding workflow',
    el('span', { class: 'count' }, `(${countOpen(items)} open)`),
  ));
  if (!items.length) {
    card.appendChild(el('p', { class: 'muted' },
      'No form response yet. Joiner needs to fill the Google Form, then run Sync everything.'));
  } else {
    const list = el('div', { class: 'stack' });
    card.appendChild(list);
    for (const item of items) {
      list.appendChild(renderOnboardingItem(employeeId, employee, item));
    }
  }
  // "Finish onboarding" — close it out without handing anything over, for
  // joiners who bring their own gear or never responded. Cancels any open
  // items and moves the person to Active (the onboarding twin of
  // Dismiss-offboarding). Always available while in the onboarding phase —
  // crucially also when there are no items to cancel (the stuck case).
  card.appendChild(renderFinishOnboarding(employeeId, employee, host));
}

function renderFinishOnboarding(employeeId, employee, host) {
  const btn = el('button', { class: 'btn btn-sm', style: 'margin-top:16px' },
    'Finish onboarding (no devices needed)');
  btn.addEventListener('click', async () => {
    if (!await confirmDialog({
      title: `Finish onboarding for ${employee.fullName || 'this person'}?`,
      body: 'Nothing is handed over. Open items are cancelled and they move to Active.',
      confirm: 'Finish onboarding',
    })) return;
    withButtonBusy(btn, async () => {
      try {
        const res = await finishOnboarding({ employeeId });
        toast(res?.cancelledCount
          ? `Onboarding finished — ${res.cancelledCount} open item${res.cancelledCount === 1 ? '' : 's'} cancelled. Moved to Active.`
          : 'Onboarding finished. Moved to Active.', 'success');
        // phase changed → full re-mount. remountPage fires 'cleanup' first so
        // this mount's three live watchers unsubscribe instead of lingering.
        remountPage(host, renderPersonDetail, employeeId);
      } catch (err) {
        console.error('[person-detail] finish onboarding failed', err);
        toast(err.message || 'Could not finish onboarding.', 'error');
      }
    });
  });
  return btn;
}

function countOpen(items) {
  return items.filter((it) => !['handed_over', 'cancelled'].includes(it.status)).length;
}

function renderOnboardingItem(employeeId, employee, item) {
  const cat = CATEGORIES[item.category] || { label: item.category };
  const status = ONBOARDING_STATUS[item.status] || ONBOARDING_STATUS.requested;
  const titleText = item.name || cat.label;
  const subtitleText = item.name ? cat.label : '';
  const subcategoryText = item.subcategory
    ? (TARIFF_SUBCATEGORIES[item.subcategory] || item.subcategory)
    : '';

  // Cancelled items stay visible (audit trail) but muted; admin can
  // Restore one if it was a mis-click. They don't count against the
  // "X open" header (see countOpen above).
  const rowClass = item.status === 'cancelled' ? 'item item-cancelled' : 'item';

  return el('div', { class: rowClass },
    el('span', { class: 'cat-icon' }, categoryIcon(item.category)),
    el('div', null,
      el('div', { class: 'name' },
        item.name ? copyable(item.name, { label: 'name' }) : titleText,
        subcategoryText
          ? el('span', { class: 'muted', style: 'margin-left:8px' }, `· ${subcategoryText}`)
          : null,
      ),
      el('div', { class: 'meta' },
        subtitleText,
        item.notes ? el('span', null, subtitleText ? ' · ' : '', `“${item.notes}”`) : null,
        item.sn ? copyable(item.sn, { label: 'SN', mono: true, style: 'margin-left:8px', display: `SN ${item.sn}` }) : null,
      ),
    ),
    el('span', { class: `chip chip-${status.badge}` }, status.label),
    el('div', { class: 'row' },
      ...nextOnboardingActions(item).map(({ label, status: nextStatus, needsSN, confirm }) => {
        const btn = el('button', { class: 'btn btn-sm' }, label);
        btn.addEventListener('click', async () => {
          // Dialogs (confirm / serial prompt) run BEFORE the busy state — no
          // loader behind a prompt, and cancelling leaves the button untouched.
          if (confirm && !await confirmDialog({ title: confirm, confirm: label })) return;
          const patch = { status: nextStatus };
          if (needsSN && cat.tracksSerial) {
            const sn = await promptText({
              title: cat.label, label: 'Serial number', value: item.sn || '', placeholder: 'C02XK1ABJG5M',
              confirmLabel: label, required: true, mono: true,
            });
            if (sn === null) return;
            patch.sn = sn;
          }
          // withButtonBusy disables the button + shows the loader for the
          // whole async op, so rapid clicks can't fire it twice (the
          // duplicate-mice bug). The server also dedupes via requestId.
          withButtonBusy(btn, async () => {
            try {
              // SIDE EFFECT FIRST, then the terminal state — the same
              // ordering the offboarding flow uses ("prevent split-brain").
              // Committing handed_over first meant a failing handoverDevice
              // left the item terminal with no device, no /handovers row and
              // no Finance ping — and `handed_over` offers no action buttons,
              // so there was no way to retry. The server dedupes on
              // requestId, so a retry after a partial failure is safe.
              if (nextStatus === 'handed_over') {
                if (ABRA_OWNED.has(item.category)) {
                  // Laptop / phone: Abra doesn't have it yet. The same
                  // `register` pending action the profile dialog submits —
                  // Finance gets the hand-over ping, the row sits in Pending
                  // Abra (cancellable) and reconciles by serial once the
                  // asset lands. Nothing is written to /devices here.
                  const res = await submitGearAction({
                    action: 'register',
                    payload: {
                      device:   { name: item.name || null, sn: patch.sn || item.sn || null, category: item.category },
                      toHolder: { employeeId, name: employee.fullName || null, email: employee.email || null },
                      onboardingItemId: item.id,
                    },
                  });
                  // Delivery is retried server-side, so this is informational.
                  if (!res?.slack?.ok) {
                    toast(`Handed over, but the Slack ping failed (${res?.slack?.error || res?.slack?.reason || 'unknown'}). Retrying automatically.`, 'error', 8000);
                  }
                } else if (item.category !== 'phone_tariff') {
                  // Peripheral: handoverDevice mints the Gear-only device +
                  // /handovers + audit row server-side. Finance isn't involved.
                  await handoverDevice({
                    employeeId,
                    employeeEmail: employee.email || null,
                    employeeName:  employee.fullName || null,
                    category:      item.category,
                    name:          item.name || null,
                    sn:            patch.sn || item.sn || null,
                    notes:         item.notes || null,
                    onboardingItemId: item.id,
                  });
                } else {
                  // Phone tariff: recordHandover writes /handovers but does
                  // not audit, so emit the audit row client-side here.
                  await recordHandover({
                    employeeId,
                    employeeName: employee.fullName || null,
                    direction:    'out',
                    category:     item.category,
                    notes:        item.notes || null,
                    actor:        getCurrentUser()?.email || null,
                  });
                  logAuditEvent({
                    action: AUDIT_ACTION.DEVICE_HANDOVER,
                    subject: { type: 'employee', id: employeeId, label: employee.fullName || null },
                    people: [employee.email],
                    details: {
                      direction: 'out',
                      category: item.category, itemName: item.name || null,
                      sn: patch.sn || item.sn || null,
                    },
                  });
                }
              }
              await updateOnboardingItem(employeeId, item.id, patch);
              logAuditEvent({
                action: AUDIT_ACTION.ONBOARDING_STATUS,
                subject: { type: 'employee', id: employeeId, label: employee.fullName || null },
                people: [employee.email],
                details: {
                  itemId:      item.id,
                  category:    item.category,
                  name:        item.name || null,
                  from:        item.status || null,
                  to:          nextStatus,
                  sn:          patch.sn || item.sn || null,
                  subcategory: item.subcategory || null,
                  notes:       item.notes || null,
                },
              });
              toast(`Marked ${nextStatus.replace('_', ' ')}.`, 'success');
              // No manual re-render — the watchOnboarding listener re-renders
              // this card from the committed snapshot.
            } catch (err) {
              console.error(err);
              toast(err.message || 'Update failed — nothing was changed. Try again.', 'error');
            }
          });
        });
        return btn;
      }),
    ),
  );
}

function nextOnboardingActions(item) {
  // Mirrors onboarding.js. Cancel exits from any non-terminal state for the
  // "joiner declined / never needed it" case; Restore is the explicit undo.
  const CANCEL = {
    label:   'Cancel',
    status:  'cancelled',
    confirm: 'Cancel this item? Use when the employee doesn\'t need it after all.',
  };
  switch (item.status) {
    case 'requested': return [{ label: 'Mark ordered',     status: 'ordered'    }, CANCEL];
    case 'ordered':   return [{ label: 'Mark prepared',    status: 'prepared',   needsSN: true }, CANCEL];
    // A laptop/phone normally got its serial at "prepared"; if it somehow
    // didn't (legacy / merged item), ask now — the register round-trip can't
    // reconcile without one. Peripherals never prompt (tracksSerial=false).
    case 'prepared':  return [{ label: 'Mark handed over', status: 'handed_over', needsSN: !item.sn }, CANCEL];
    case 'cancelled': return [{ label: 'Restore',          status: 'requested'  }];
    default:          return [];
  }
}

// ── Offboarding workflow (inline) ──────────────────────────────────────
let offboardingRenderSeq = 0;
async function renderOffboardingWorkflow(card, employeeId, employee, devices, formulas) {
  // Re-fetch /offboardings each render. The row buttons mutate
  // deviceDecisions inside this same doc and then call onChange to re-
  // render this card; if we cached `offboarding` in a parameter, the
  // re-render would see the stale pre-click state and the UI would
  // appear frozen (button text doesn't advance until full-page refresh).
  // One getDoc per render is cheap.
  //
  // FETCH BEFORE CLEAR, plus a generation token: this used to clear() first
  // and then await, so a devices snapshot arriving mid-fetch started a second
  // render and both appended — two stacked workflow cards, the stale one
  // carrying pre-click button state.
  const seq = ++offboardingRenderSeq;
  const offboarding = await getOffboarding(employeeId).catch(() => null);
  if (seq !== offboardingRenderSeq || !card.isConnected) return; // superseded
  clear(card);
  const decisions = offboarding?.deviceDecisions || {};
  card.appendChild(el('div', { class: 'section-head' },
    'Offboarding workflow',
    el('span', { class: 'count' }, `(${devices.length} device${devices.length === 1 ? '' : 's'})`),
  ));
  if (!devices.length) {
    card.appendChild(el('p', { class: 'muted' },
      "No devices to settle. If the leaver should have some, the assignment lives in Abra — fix it there and run Sync everything."));
    return;
  }
  const list = el('div', { class: 'stack' });
  card.appendChild(list);
  let total = 0;
  for (const device of devices) {
    const decision = decisions[device.id] || { status: 'pending' };
    const resale = calculateResaleValue(device, formulas);
    total += resale || 0;
    list.appendChild(renderOffboardingDeviceRow(employeeId, employee, device, decision, resale,
      () => renderOffboardingWorkflow(card, employeeId, employee, devices, formulas)));
  }
  card.appendChild(el('p', { class: 'muted', style: 'margin-top:16px;border-top:1px solid var(--color-surface-pressed);padding-top:12px' },
    `Total resale value: ${total > 0 ? formatCZK(total) : '—'}`));
}

function renderOffboardingDeviceRow(employeeId, employee, device, decision, resale, onChange) {
  const status = OFFBOARDING_STATUS[decision.status] || OFFBOARDING_STATUS.pending;
  const chips = (device.contextTags || []).map((t) => {
    const info = contextTagInfo(t);
    return el('span', { class: 'chip' }, info.label);
  });
  // Display the price the admin actually entered when the decision was
  // finalised (bought / to_loot), falling back to the formula suggestion
  // otherwise. The previous version always showed the formula value,
  // which made finalised rows look like the admin's manual override hadn't
  // been honoured.
  const displayedPrice = decision.enteredPriceCzk != null ? decision.enteredPriceCzk : resale;
  return el('div', { class: 'item' },
    el('span', { class: 'cat-icon' }, categoryIcon(device.category)),
    el('div', null,
      el('div', { class: 'name' }, device.name ? copyable(device.name, { label: 'name' }) : device.category,
        chips.length ? el('span', { style: 'margin-left:8px' },
          el('span', { class: 'chips', style: 'display:inline-flex' }, ...chips)) : null),
      el('div', { class: 'meta' },
        device.purchaseDate ? `Bought ${formatDate(device.purchaseDate)}` : 'Purchase date unknown',
        device.sn ? copyable(device.sn, { label: 'SN', mono: true, style: 'margin-left:8px', display: `SN ${device.sn}` }) : '',
      ),
    ),
    el('span', { class: 'price' }, displayedPrice != null ? formatCZK(displayedPrice) : '—'),
    el('span', { class: `chip chip-${status.badge}` }, status.label),
    el('div', { class: 'row' },
      ...nextOffboardingActions(decision, device).map(({ label, status: next, requireConfirm }) => {
        const btn = el('button', { class: 'btn btn-sm' }, label);
        btn.addEventListener('click', async () => {
          // Price prompt / confirm run BEFORE the busy state (dialogs, not
          // writes). Cancelling leaves the button untouched.
          let enteredPrice = null;
          if (TERMINAL_NEEDS_PRICE.has(next)) {
            enteredPrice = await promptForPrice({
              title:        `Sale price — ${device.name || 'device'}`,
              subtitle:     `Sale price (excl. VAT) — Finance adds VAT on the invoice. Buyer: ${employee.fullName || 'this employee'}. Goes into the sales CSV export for Finance.`,
              label:        'Sale price',
              suggested:    resale || 0,
              priceNote:    'Defaults to the formula suggestion. Admins can override.',
              confirmLabel: 'Mark bought',
            });
            if (enteredPrice == null) return;
          } else if (next === 'to_loot') {
            if (!await confirmDialog({
              title: `Send ${device.name || 'this device'} to Loot?`,
              body: 'Price and condition are set in Loot.',
              confirm: 'Send to Loot',
            })) return;
          } else if (requireConfirm && !await confirmDialog({ title: `${label}?`, confirm: label })) {
            return;
          }
          const actor = getCurrentUser()?.email || null;
          const gearAction = OFFBOARDING_TERMINAL_TO_GEAR_ACTION[next];
          // Busy loader + re-entrancy guard over the writes (prevents a
          // double-click firing the gear action / decision twice).
          await withButtonBusy(btn, async () => {
            try {
              // ── Step 1: the Abra-reconciliation action is authoritative.
              // If a terminal state maps to a gear action (sell/takeback/
              // make_testing) — or is Send to Loot — fire it FIRST. Only if it succeeds
              // do we record the local decision + handover. This prevents
              // the "device shows sold locally but Finance was never
              // pinged and no pending row exists" split-brain: if the
              // submit throws, we abort here and the device stays in its
              // prior state for a clean retry.
              if (gearAction) {
                const payload = {};
                if (gearAction === 'sell') {
                  payload.saleData = {
                    buyer:             { employeeId, name: employee.fullName || null, email: employee.email || null },
                    salePriceCzk:      enteredPrice,
                    suggestedPriceCzk: resale ?? null,
                    isExternal:        false,
                  };
                } else if (gearAction === 'takeback') {
                  payload.fromHolder = { employeeId, name: employee.fullName || null };
                }
                const res = await submitGearAction({ action: gearAction, deviceId: device.id, payload });
                if (!res.slack?.ok) {
                  toast(`Submitted, BUT Slack delivery failed — ${res.slack?.error || res.slack?.reason || 'unknown'}. Will retry automatically (or use Retry on Pending Abra).`, 'error', 8000);
                }
                // Finance CSV in hand immediately for an owner sale.
                if (gearAction === 'sell') {
                  const csvRow = {
                    deviceName:   device.name || '',
                    deviceSn:     device.sn   || '',
                    salePriceCzk: enteredPrice,
                    buyerEmail:   employee.email || '',
                  };
                  const filename = downloadFinanceCsv([csvRow]);
                  if (filename) toast(`Downloaded ${filename} for Finance.`, 'success', 5000);
                }
              } else if (next === 'to_loot') {
                // Send to Loot through the IT Hub — same as the device page.
                reportLootResult(await sendToLoot({
                  deviceId: device.id,
                  note: `Returned by ${employee.fullName || employee.email || 'a leaver'} (offboarding).`,
                }));
              }

              // ── Step 2: record the local decision (drives the workflow
              // UI). Store the entered price so /offboardings is the
              // source of truth for sale/loot price in audit terms.
              await setDeviceDecision(employeeId, device.id, {
                status:       next,
                resaleValue:  resale ?? null,
                ...(enteredPrice != null ? { enteredPriceCzk: enteredPrice } : {}),
              }, actor);
              logAuditEvent({
                action: AUDIT_ACTION.OFFBOARDING_DEVICE,
                subject: { type: 'device', id: device.id, label: device.name || null },
                people: [employee.email],
                details: {
                  employeeId, employeeName: employee.fullName || null,
                  from: decision.status || 'pending', to: next,
                  resaleValue: resale ?? null,
                  enteredPriceCzk: enteredPrice,
                },
              });

              // ── Step 3: terminal states log a /handovers row.
              if (TERMINAL_OFFBOARDING_STATUSES.has(next)) {
                await recordHandover({
                  employeeId,
                  deviceId:     device.id,
                  direction:    'in',
                  category:     device.category,
                  sn:           device.sn || null,
                  resaleValue:  resale ?? null,
                  outcome:      next,
                  actor,
                });
                logAuditEvent({
                  action: AUDIT_ACTION.DEVICE_HANDOVER,
                  subject: { type: 'device', id: device.id, label: device.name || null },
                  people: [employee.email],
                  details: {
                    direction: 'in', outcome: next,
                    employeeId, employeeName: employee.fullName || null,
                    sn: device.sn || null, resaleValue: resale ?? null,
                  },
                });
              }

              toast(`Marked ${next.replace('_', ' ')}.`, 'success');
              onChange();
            } catch (err) {
              console.error('[offboarding] terminal action failed', err);
              toast(
                gearAction
                  ? `Couldn't submit to Finance: ${err.message || 'unknown error'}. Device unchanged — try again.`
                  : (err.message || 'Update failed.'),
                'error', 8000,
              );
            }
          });
        });
        return btn;
      }),
    ),
  );
}

function nextOffboardingActions(decision, _device) {
  // Phone tariffs no longer flow through the device state machine —
  // they're rendered in a separate section driven by Bamboo
  // customAssets (see renderPhoneTariffWorkflow). The buy/return chain
  // below applies to physical devices only.
  switch (decision.status) {
    case 'pending':  return [{ label: 'Mark asked',  status: 'informed' }];
    case 'informed': return [
      { label: 'Will buy',    status: 'buy'    },
      { label: 'Will return', status: 'return' },
    ];
    case 'buy':      return [{ label: 'Mark bought',   status: 'bought' }];
    case 'return':   return [{ label: 'Mark returned', status: 'returned' }];
    case 'returned': return [
      { label: 'Move to storage',  status: 'in_storage'  },
      { label: 'Send to Loot',     status: 'to_loot'     }, // confirmed in the click handler
      { label: 'Keep for testing', status: 'to_testing'  },
    ];
    default: return [];
  }
}

// ── Phone-tariff workflow (Bamboo customAssets-driven) ────────────────
async function renderPhoneTariffWorkflow(card, employeeId, employee, assets, showActions) {
  // Re-fetch offboarding for fresh assetDecisions; onChange re-renders just
  // this card (no full-page re-mount). `assets` come from the employee doc
  // (Bamboo) and don't change on a decision, so they're passed through.
  const offboarding = await getOffboarding(employeeId).catch(() => null);
  const onChange = () => renderPhoneTariffWorkflow(card, employeeId, employee, assets, showActions);
  clear(card);
  const decisions = offboarding?.assetDecisions || {};
  card.appendChild(el('div', { class: 'section-head' },
    'Phone tariff',
    el('span', { class: 'count' }, `(${assets.length} active)`),
  ));
  card.appendChild(el('p', { class: 'muted', style: 'margin:0 0 12px' },
    showActions
      ? 'The line is still active, so this stays open until it has actually been returned or cancelled.'
      : 'Workflow controls appear once the employee enters offboarding.'));
  const list = el('div', { class: 'stack' });
  card.appendChild(list);
  for (const asset of assets) {
    const decision = decisions[asset.bambooAssetId] || { status: 'pending' };
    list.appendChild(renderPhoneTariffRow(employeeId, employee, asset, decision, showActions, onChange));
  }
}

function renderPhoneTariffRow(employeeId, employee, asset, decision, showActions, onChange) {
  const status = OFFBOARDING_STATUS[decision.status] || OFFBOARDING_STATUS.pending;
  return el('div', { class: 'item' },
    el('span', { class: 'cat-icon' }, categoryIcon('phone_tariff')),
    el('div', null,
      el('div', { class: 'name' }, asset.description ? copyable(asset.description, { label: 'number' }) : 'Sim Card (no number)'),
      el('div', { class: 'meta' },
        asset.dateLoaned ? `Loaned ${formatDate(asset.dateLoaned)}` : 'Loan date unknown',
        asset.serial ? el('span', { class: 'sn', style: 'margin-left:8px;font-family:var(--font-serial);font-size:var(--fs-small)' }, `SN ${asset.serial}`) : null,
      ),
    ),
    el('span'), // price slot — phone tariffs have none
    showActions ? el('span', { class: `chip chip-${status.badge}` }, status.label) : el('span'),
    el('div', { class: 'row' },
      ...(showActions ? nextPhoneTariffActions(decision) : []).map(({ label, status: next }) =>
        el('button', {
          class: 'btn btn-sm',
          onClick: async () => {
            try {
              const actor = getCurrentUser()?.email || null;
              await setAssetDecision(employeeId, asset.bambooAssetId, { status: next }, actor);
              logAuditEvent({
                action: AUDIT_ACTION.OFFBOARDING_ASSET,
                subject: { type: 'employee', id: employeeId, label: employee.fullName || null },
                people: [employee.email],
                details: {
                  bambooAssetId: asset.bambooAssetId,
                  description:   asset.description || null,
                  from: decision.status || 'pending', to: next,
                },
              });
              if (PHONE_TARIFF_TERMINAL_STATUSES.includes(next)) {
                await recordHandover({
                  employeeId,
                  bambooAssetId: asset.bambooAssetId,
                  direction:     'in',
                  category:      'phone_tariff',
                  description:   asset.description || null,
                  outcome:       next,
                  actor,
                });
                logAuditEvent({
                  action: AUDIT_ACTION.DEVICE_HANDOVER,
                  subject: { type: 'employee', id: employeeId, label: employee.fullName || null },
                  people: [employee.email],
                  details: {
                    direction: 'in', outcome: next,
                    category: 'phone_tariff',
                    bambooAssetId: asset.bambooAssetId,
                    description:   asset.description || null,
                  },
                });
              }
              toast(`Marked ${next.replace('_', ' ')}.`, 'success');
              onChange();
            } catch (err) {
              console.error(err);
              toast(err.message || 'Update failed.', 'error');
            }
          },
        }, label),
      ),
    ),
  );
}

function nextPhoneTariffActions(decision) {
  switch (decision.status) {
    case 'pending':            return [{ label: 'Mark asked',              status: 'informed' }];
    case 'informed':           return [
      { label: 'Wants to keep',   status: 'wants_to_keep'   },
      { label: 'Wants to cancel', status: 'wants_to_cancel' },
    ];
    case 'wants_to_keep':      return [{ label: 'Mark transfer requested', status: 'transfer_requested' }];
    case 'wants_to_cancel':    return [{ label: 'Mark cancel requested',   status: 'cancel_requested'   }];
    case 'transfer_requested': return [{ label: 'Mark transferred',        status: 'transferred'        }];
    case 'cancel_requested':   return [{ label: 'Mark disconnected',       status: 'disconnected'       }];
    default: return [];
  }
}

// ── Shared ─────────────────────────────────────────────────────────────
function renderHandoverRow(h) {
  const when = formatDate(h.ts);
  const dir = h.direction === 'in' ? 'retrieved' : (h.direction === 'transfer' ? 'transferred' : 'handed over');
  return el('div', { class: 'row history-row', style: 'border-bottom:1px solid var(--color-surface-pressed);padding:8px 0;font-size:var(--fs-small)' },
    el('span', { class: 'history-when', style: 'width:110px;color:var(--color-text-tertiary)' }, when),
    el('span', { style: 'flex:1' },
      `${dir}${h.category ? `: ${h.category}` : ''}`,
      h.sn ? el('span', null, ' (', copyable(h.sn, { label: 'SN', mono: true, display: `SN ${h.sn}` }), ')') : '',
      `${h.outcome ? ` - ${h.outcome}` : ''}${h.note ? ` · ${h.note}` : ''}`),
    el('span', { class: 'muted' }, h.actor || ''),
  );
}

function backLink() {
  // Context-aware: goes back to wherever the person was opened from
  // (Onboarding, Offboarding, a device page, …); '#/people' on deep link.
  return renderBackLink({ fallbackHref: '#/people', fallbackLabel: 'Back to people' });
}

// ── Hand over a device (from the profile, any time) ─────────────────────
// Two different things behind one dialog, split by category:
//   • Laptops & phones are Abra-owned. Nothing is created in Gear; the submit
//     writes a `register` pending-changes row (serial required) and pings
//     Finance with the same hand-over message onboarding uses. The row sits
//     under "Pending in Abra" here and on #/pending until the next sync sees
//     the asset in Abra assigned to this person — then it reconciles and the
//     device shows under "Currently held" like any other.
//   • Peripherals (keyboard / mouse / monitor / charger…) are Gear-only:
//     handoverDevice mints a gear-source /devices doc on the spot, so it shows
//     under "Currently held" immediately. Finance isn't involved.
// This is how a laptop/phone reaches someone OUTSIDE onboarding — a renewal,
// a first phone a year in, a replacement — without a separate flow. Taking
// the old device back stays a separate action on that device's page: it
// isn't always device-for-device.
const HANDOVER_CATEGORIES = ['laptop', 'phone', 'ext_screen', 'keyboard', 'mouse', 'charger', 'access_card', 'other'];

function openAddGearModal(employeeId, employee) {
  const backdrop = el('div', { class: 'modal-backdrop open' });
  const categorySelect = el('select', null,
    ...HANDOVER_CATEGORIES.map((k) => el('option', { value: k }, (CATEGORIES[k] || {}).label || k)),
  );
  const nameInput = el('input', { type: 'text', placeholder: 'MacBook Pro 14" M4' });
  const snInput   = el('input', { type: 'text', class: 'input-serial', placeholder: 'C02XK1ABJG5M', autocapitalize: 'characters', autocorrect: 'off', spellcheck: 'false' });
  const snLabel   = el('label', null, 'Serial number');
  const close = () => backdrop.remove();

  // The serial is mandatory for Abra-owned categories — it is the only key
  // the round-trip (Finance registers → sync → reconcile) can match on. Typed
  // as printed on the device; Finance may or may not add the distributor's
  // leading "S" in Abra, and normaliseSnForMatch treats both as one key.
  const syncCategoryUi = () => {
    snLabel.textContent = ABRA_OWNED.has(categorySelect.value) ? 'Serial number *' : 'Serial number';
  };
  categorySelect.addEventListener('change', syncCategoryUi);
  syncCategoryUi();

  const submitBtn = el('button', { class: 'btn btn-primary' }, 'Hand over');
  submitBtn.addEventListener('click', () => {
    const category = categorySelect.value;
    const name = nameInput.value.trim() || null;
    const sn   = snInput.value.trim() || null;
    // Validation before the busy state (same rule as the onboarding items).
    if (ABRA_OWNED.has(category) && !sn) {
      toast('Serial number is required.', 'error');
      snInput.focus();
      return;
    }
    withButtonBusy(submitBtn, async () => {
      try {
        if (ABRA_OWNED.has(category)) {
          const res = await submitGearAction({
            action: 'register',
            payload: {
              device:   { name, sn, category },
              toHolder: { employeeId, name: employee.fullName || null, email: employee.email || null },
            },
          });
          if (res?.slack?.ok) {
            toast('Handed over. Finance notified.', 'success');
          } else {
            toast(`Handed over, but the Slack ping failed (${res?.slack?.error || res?.slack?.reason || 'unknown'}). Retrying automatically.`, 'error', 8000);
          }
        } else {
          await handoverDevice({
            employeeId,
            employeeEmail: employee.email || null,
            employeeName:  employee.fullName || null,
            category,
            name,
            sn,
          });
          toast('Device handed over.', 'success');
        }
        close();
      } catch (err) {
        console.error('[person-detail] hand-over failed', err);
        toast(err.message || 'Could not hand over the device.', 'error');
      }
    });
  });

  backdrop.appendChild(el('div', { class: 'modal' },
    el('h2', null, 'Hand over a device'),
    el('p', { class: 'muted', style: 'margin-top:0' }, `To ${employee.fullName || 'this person'}.`),
    el('div', { class: 'stack', style: 'margin-top:16px' },
      el('div', { class: 'field' }, el('label', null, 'Category'),     categorySelect),
      el('div', { class: 'field' }, el('label', null, 'Name / model'), nameInput),
      el('div', { class: 'field' }, snLabel,                            snInput),
    ),
    el('div', { class: 'row', style: 'margin-top:24px;justify-content:flex-end;gap:8px' },
      el('button', { class: 'btn btn-ghost', onClick: close }, 'Cancel'),
      submitBtn,
    ),
  ));
  document.body.appendChild(backdrop);
  categorySelect.focus();
}

function phaseLabel(p) {
  switch (p) {
    case PHASES.onboarding:  return 'Onboarding';
    case PHASES.active:      return 'Active';
    case PHASES.offboarding: return 'Offboarding';
    case PHASES.done:        return 'Done';
    default:                 return p || '-';
  }
}

function phaseClass(p) {
  switch (p) {
    case PHASES.onboarding:  return 'chip-warning';
    case PHASES.active:      return 'chip-ready';
    case PHASES.offboarding: return 'chip-error';
    case PHASES.done:        return 'chip-idle';
    default:                 return 'chip-idle';
  }
}

